import { motion, useReducedMotion } from "framer-motion";
import { useTab } from "@/hooks/useTab";
import { useFilters } from "@/hooks/useFilters";
import { cn } from "@/lib/cn";
import { fmt } from "@/lib/format";
import { medalLabel } from "@/lib/theme";
import { Badge } from "@/components/cards/Badge";
import { EmptyState, ErrorState, SectionTitle, TabSkeleton } from "./shared";
import type { MoverRow } from "@/api/types";

type Direction = "up" | "down";

interface MoverListProps {
  rows: MoverRow[];
  direction: Direction;
}

function DeltaPill({ delta, direction }: { delta: number; direction: Direction }) {
  const reducedMotion = useReducedMotion();
  const positive = delta > 0;
  const arrow = direction === "up" ? "↑" : "↓";
  const color =
    direction === "up"
      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
      : "bg-rose-500/15 text-rose-300 border-rose-500/30";
  return (
    <motion.div
      initial={reducedMotion ? false : { y: direction === "up" ? 8 : -8, opacity: 0, scale: 0.8 }}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      transition={
        reducedMotion
          ? { duration: 0 }
          : { type: "spring", stiffness: 420, damping: 18 }
      }
      className={cn(
        "inline-flex h-8 min-w-[3.5rem] items-center justify-center gap-1 rounded-full border px-2 text-sm font-bold tabular",
        color,
      )}
      aria-label={`Variación de ranking ${positive ? "+" : ""}${delta}`}
    >
      <span className="text-base leading-none">{arrow}</span>
      <span>
        {positive ? "+" : ""}
        {delta}
      </span>
    </motion.div>
  );
}

function MoverRowCard({
  row,
  direction,
  idx,
}: {
  row: MoverRow;
  direction: Direction;
  idx: number;
}) {
  const reducedMotion = useReducedMotion();
  const initial = (row.username ?? "?").charAt(0).toUpperCase();
  const rank = row.rank_now ?? row.rank ?? idx + 1;
  const rankIsMedal = rank > 0 && rank <= 3;
  const isNew = row.flag === "new";
  const isInactive = row.flag === "inactive";

  return (
    <motion.div
      initial={reducedMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        reducedMotion
          ? { duration: 0 }
          : { duration: 0.32, ease: "easeOut", delay: idx * 0.05 }
      }
      className={cn(
        "flex items-center gap-4 rounded-[14px] border border-border-1 bg-white/[0.02] px-4 py-3 transition-colors",
        "hover:bg-white/[0.04] hover:border-border-2",
        isInactive && "opacity-60",
      )}
    >
      <DeltaPill delta={row.rank_delta} direction={direction} />

      <div
        className={cn(
          "flex h-8 w-10 items-center justify-center rounded-full text-xs font-bold tabular",
          rankIsMedal
            ? "bg-gradient-to-br from-brand-1 to-brand-3 text-white"
            : "bg-white/5 text-text-3",
        )}
        aria-label={`Posición ${rank}`}
      >
        {medalLabel(rank)}
      </div>

      <div
        className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-sm font-semibold text-text-2"
        aria-hidden
      >
        {initial}
        {isNew && !reducedMotion && (
          <span
            aria-hidden
            className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-brand-3"
            style={{
              boxShadow: "0 0 8px 2px rgba(236,72,153,0.6)",
              animation: "mover-sparkle 1.8s ease-in-out infinite",
            }}
          />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold text-text-1">{row.username}</div>
        {row.team && <div className="truncate text-xs text-text-3">{row.team}</div>}
      </div>

      {row.badges.length > 0 && (
        <div className="flex flex-shrink-0 flex-wrap gap-1">
          {row.badges.map((b) => (
            <Badge key={b} code={b} />
          ))}
          {row.flag && <Badge code={row.flag} />}
        </div>
      )}
      {row.badges.length === 0 && row.flag && (
        <div className="flex flex-shrink-0 flex-wrap gap-1">
          <Badge code={row.flag} />
        </div>
      )}

      <div className="flex-shrink-0 text-right">
        <div className="tabular text-lg font-bold font-display text-text-1">
          {fmt(row.msgs_now)}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-text-4">
          mensajes
        </div>
      </div>

      <style>
        {`@keyframes mover-sparkle {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.35); }
        }`}
      </style>
    </motion.div>
  );
}

function MoverList({ rows, direction }: MoverListProps) {
  return (
    <div className="flex flex-col gap-2">
      {rows.map((row, idx) => (
        <MoverRowCard key={row.uid} row={row} direction={direction} idx={idx} />
      ))}
    </div>
  );
}

/** Tab 4 — Top climbers (up) + top fallers (down) between periods. */
export function MoversTab() {
  const { filters } = useFilters();
  const { data, isPending, error } = useTab("movers", filters);

  if (isPending) return <TabSkeleton />;
  if (error) return <ErrorState error={error} />;
  if (!data) return <EmptyState title="Sin datos de movimientos." />;

  return (
    <div className="grid grid-cols-2 gap-8">
      <section>
        <SectionTitle>Suben más</SectionTitle>
        {data.up.length === 0 ? (
          <EmptyState
            title="Nadie escala esta semana."
            description="¿Serás tú la próxima?"
          />
        ) : (
          <MoverList rows={data.up} direction="up" />
        )}
      </section>

      <section>
        <SectionTitle>Bajan más</SectionTitle>
        {data.down.length === 0 ? (
          <EmptyState
            title="Nadie cae esta semana."
            description="Toda la plantilla mantiene el ritmo."
          />
        ) : (
          <MoverList rows={data.down} direction="down" />
        )}
      </section>
    </div>
  );
}
